/**
 * Play frame.
 *
 * SHELL-040 to SHELL-046 (goals HUD, pause/restart/quit, landscape layout) are
 * still open -- this wires the loader and the game contract and nothing else.
 * `Do not build a HUD, stars, or progression` (Sprint 1b) is why `onComplete`
 * leads to one plain message and a link rather than a result screen.
 *
 * The unknown-game branch (SHELL-013) is a plain check with no hooks before
 * it, for the same reason `LevelSelect` splits the same way: `gameId` and
 * `levelId` can change across renders of the same route without breaking the
 * rules of hooks, because the hook-using half lives in `PlayLoaded`.
 *
 * Resuming (`PROG-030`-`035`, `SHELL-017`) is decided once, the first render
 * `readyLevel` exists, and held in a ref rather than re-read every render:
 * this screen's own backgrounding handler writes to the same save document it
 * reads from, and re-deriving "is a level in progress" from a document this
 * screen itself keeps mutating would eventually see its own write and offer
 * to resume the level the player is already playing.
 */

import { useEffect, useRef, useState } from 'preact/hooks';
import { useLocation } from 'preact-iso';
import { signal } from '@preact/signals';
import type { Signal } from '@preact/signals';

import type { GameContext, GameProgress, GameResult } from '@contracts/game-module';
import type { InProgress, LevelRecord, SaveDocument } from '@contracts/save';
import { isKnownGame } from '@platform/game-registry';
import { announce } from '@platform/announce';
import type { LoadFailure } from '@platform/levels';
import { loadLevel } from '@platform/levels';
import { requestPersistentStorage } from '@platform/persistent-storage';
import { deriveStars, isFirstCompletionEver, recordCompletion } from '@shell/completion';
import { Hud } from '@shell/Hud';
import { nextLevel } from '@shell/progression';
import { ResultScreen } from '@shell/ResultScreen';
import { LeaveConfirmDialog } from '@shell/LeaveConfirmDialog';
import { PauseOverlay } from '@shell/PauseOverlay';
import { QuitConfirmDialog } from '@shell/QuitConfirmDialog';
import { RestartConfirmDialog } from '@shell/RestartConfirmDialog';
import { Screen } from '@shell/Screen';
import { routes } from '@shell/routes';
import { NotFound } from '@shell/screens/NotFound';
import { loadFailureMessage } from '@shell/load-failure-message';
import { levelInProgressSignal } from '@shell/level-in-progress';
import { currentDocument, persist, persistInProgressSync } from '@shell/save';
import { useGameModule } from '@shell/use-game-module';
import type { Level } from '@contracts/level';

export function Play({ gameId, levelId }: { gameId: string; levelId: string }) {
  if (!isKnownGame(gameId)) return <NotFound />;
  return <PlayLoaded gameId={gameId} levelId={levelId} />;
}

type LevelState =
  | { status: 'loading' }
  | { status: 'ready'; level: Level }
  | { status: 'error'; failure: LoadFailure };

/** Not yet looked at the save document -- distinct from "looked, and it was `null`". */
const UNCHECKED = Symbol('in-progress not yet checked');

function PlayLoaded({ gameId, levelId }: { gameId: string; levelId: string }) {
  const moduleState = useGameModule(gameId);
  const readyGame = moduleState.status === 'ready' ? moduleState.game : undefined;
  const readyIndex = moduleState.status === 'ready' ? moduleState.index : undefined;

  const [levelState, setLevelState] = useState<LevelState>({ status: 'loading' });
  const [result, setResult] = useState<GameResult | null>(null);
  // `PROG-042`/`043`: the cumulative star count `recordCompletion` just
  // persisted, not one re-derived from `result` alone -- stars are
  // permanent, so what `ResultScreen` shows has to be the save document's
  // own number, captured from the same call that writes it (below), not a
  // second formula that could disagree with it.
  const [stars, setStars] = useState(0);
  const hostRef = useRef<HTMLDivElement | null>(null);
  const playAreaRef = useRef<HTMLDivElement | null>(null);
  const location = useLocation();

  // SHELL-040. `Signal`s, not `useState` -- `Hud.tsx`'s own comment has the
  // reasoning: a tick of either must re-render the HUD alone, never this
  // component's tree (`SHELL-045`), and Preact's signals only give that
  // guarantee to a value read as a `Signal`, not to state lifted here and
  // passed down already unwrapped. Created once, for the component's whole
  // life, and reset to a clean slate at the top of the mounting effect below
  // -- not per mount -- so their identity stays stable across every restart
  // and level change, which is what lets `Hud` subscribe once rather than
  // resubscribing on every remount.
  const progressSignalRef = useRef<Signal<GameProgress | null>>();
  progressSignalRef.current ??= signal<GameProgress | null>(null);
  const progressSignal = progressSignalRef.current;
  const remainingMsSignalRef = useRef<Signal<number | null>>();
  remainingMsSignalRef.current ??= signal<number | null>(null);
  const remainingMsSignal = remainingMsSignalRef.current;

  // SHELL-081's caller lives in the timer effect further down; this is what
  // it calls. A ref, not a second piece of context state, because the
  // handler itself is rebuilt by `ctx.onTimeExpired` every mount and the
  // timer effect has no reason to be in that effect's own dependency array.
  const timeExpiredHandlerRef = useRef<(() => void) | null>(null);

  // SHELL-041/SHELL-042/SHELL-043. `restartTrigger` forces the mounting
  // effect below to tear down and remount even though none of `readyGame`,
  // `readyLevel`, `gameId`, `levelId` or `resumeChoice` changed -- a restart
  // is a fresh attempt at the same level, not a navigation; its value carries
  // no meaning beyond "changed". `forceFreshMountRef` is the actual
  // instruction ("ignore whatever `resumeChoice` decided"), read and reset
  // to `false` inside the effect on every run so it cannot leak into a later,
  // unrelated mount -- a plain `restartTrigger > 0` comparison would
  // incorrectly keep forcing a fresh start after the player later navigates
  // to a different level, once `restartTrigger` is nonzero even once.
  //
  // Quit needs neither an effect-local closure nor a ref the way restart's
  // remount trigger and the timer's expiry handler do: `gameId`, `levelId`
  // and `location` are already component-level values (the last is this
  // component's own `useLocation()` call above), so its handler is defined
  // entirely at component level, further down.
  const [paused, setPaused] = useState(false);
  const [restartConfirmOpen, setRestartConfirmOpen] = useState(false);
  const [quitConfirmOpen, setQuitConfirmOpen] = useState(false);
  const [restartTrigger, setRestartTrigger] = useState(0);
  const forceFreshMountRef = useRef(false);

  // DATA-031/DATA-032: fetch and validate the one level about to be played,
  // and no other. `readyIndex` only carries entries, not files, so the file
  // this game emitted for `levelId` still has to be looked up before
  // `loadLevel` can fetch anything.
  useEffect(() => {
    if (!readyGame || !readyIndex) return;

    const entry = readyIndex.levels.find((candidate) => candidate.id === levelId);
    if (!entry) {
      setLevelState({
        status: 'error',
        failure: { cause: 'missing', detail: `game ${gameId} has no level named ${levelId}` },
      });
      return;
    }

    let cancelled = false;
    setLevelState({ status: 'loading' });
    void loadLevel(readyGame, entry.file).then((loaded) => {
      if (cancelled) return;
      setLevelState(
        loaded.ok
          ? { status: 'ready', level: loaded.value }
          : { status: 'error', failure: loaded.failure },
      );
    });

    return () => {
      cancelled = true;
    };
  }, [readyGame, readyIndex, gameId, levelId]);

  const readyLevel = levelState.status === 'ready' ? levelState.level : undefined;

  // PROG-030/034/035: the save document's one global in-progress slot, read
  // exactly once -- see the file header for why a ref rather than a fresh
  // read every render.
  const initialInProgressRef = useRef<InProgress | null | typeof UNCHECKED>(UNCHECKED);
  if (readyLevel && initialInProgressRef.current === UNCHECKED) {
    initialInProgressRef.current = currentDocument().inProgress;
  }
  const initialInProgress =
    initialInProgressRef.current === UNCHECKED ? null : initialInProgressRef.current;
  const sameLevel = initialInProgress?.gameId === gameId && initialInProgress.levelId === levelId;
  // PROG-066. `state` is `undefined` for a record backgrounded before the
  // game ever called `ctx.onStateChange` -- match3's own `mount.tsx` only
  // calls it after a resolved swap's cascade finishes, never at mount and
  // never for a rejected one, so a level opened and left without a
  // successful match writes a real, matching-gameId-and-levelId record with
  // nothing to restore. `mount.tsx`'s `restore(saved, ...)` cannot tell that
  // apart from `saved === undefined` meaning "no resume was attempted at
  // all" (an ordinary fresh mount), which is why offering it produced a
  // silent fresh board with no error -- found on a real device, reproduced
  // on the desktop preview in the resume-defect loop's stage 0. The fix is
  // the same predicate on both sides: nothing to offer.
  const resumableHere =
    sameLevel && initialInProgress.state !== undefined ? initialInProgress : null;
  // PROG-035. The same predicate as `resumableHere` above, on the other
  // side of the same record: a stateless record (backgrounded before the
  // first accepted move, PROG-066's own case) has nothing to discard, so it
  // must not trigger this warning either. Before this fix the gate was
  // `initialInProgress && !sameLevel` alone -- an id mismatch with no state
  // check -- so opening a level while a *different*, never-played level sat
  // stateless in the save slot warned the player about progress that did
  // not exist. PROG-066 fixed this same predicate pair's resume side and
  // left this side on its old test.
  const otherInProgress =
    initialInProgress && !sameLevel && initialInProgress.state !== undefined
      ? initialInProgress
      : null;

  const [resumeChoice, setResumeChoice] = useState<'pending' | 'resume' | 'fresh'>('pending');
  const [restoreFailed, setRestoreFailed] = useState(false);
  // SHELL-017's dialog. Holds the two answers as closures built by the
  // popstate handler below, since that is where `playUrl`, `phantomLive` and
  // `persistInProgress` live -- `null` means no confirmation is showing.
  const [leaveConfirm, setLeaveConfirm] = useState<{
    onStay: () => void;
    onLeave: () => void;
  } | null>(null);

  const awaitingOffer = Boolean(resumableHere) && resumeChoice === 'pending';
  const awaitingWarning = Boolean(otherInProgress) && resumeChoice === 'pending';

  // Mounts exactly once per (game, level) pair that becomes ready, and only
  // once any resume offer or discard warning above has been answered. A throw
  // from `game.mount()` -- `goal.ts` on a level with no custom goal is the
  // known case -- happens here, inside an effect, and Preact hands it to the
  // nearest `ErrorBoundary` (SHELL-006) the same way a render throw would --
  // except on a resume attempt specifically, PROG-032's territory, handled
  // below rather than left to that boundary.
  useEffect(() => {
    if (!readyGame || !readyLevel) return;
    if (awaitingOffer || awaitingWarning) return;
    const host = hostRef.current;
    if (!host) return;
    const level = readyLevel;

    // A clean slate for every fresh mount -- initial, resumed, restarted or
    // a different level entirely. The timer effect below reinitialises
    // `remainingMsSignal` from `level.constraints.timeLimitMs` the moment it
    // next runs and finds `null` here; declared before that effect so the
    // reset lands first within the same commit.
    progressSignal.value = null;
    remainingMsSignal.value = null;

    let torn = false;
    let teardown: () => void = () => undefined;
    // PROG-031's "state most recently reported" -- kept here rather than in a
    // signal because nothing outside this effect needs it: the backgrounding
    // and back-navigation handlers below are the only readers, in the same
    // closure.
    let latestState: unknown;

    // SHELL-017's guard, pushed now so it is in place before buildCtx below is
    // ever reached -- see the popstate listener further down for what it is
    // for. `phantomLive` tracks whether it is still sitting in the history
    // stack unconsumed, since two different paths can consume it: a real back
    // press (the listener), or the level simply ending (`onComplete`, just
    // below) while it was never pressed at all. Left live, a level that ends
    // by finishing rather than by backing out would leave it stranded behind
    // whatever the player navigates to next, silently absorbing that
    // screen's *next* back press instead of the one it was meant to guard.
    const playUrl = location.url;
    history.pushState(null, '', playUrl);
    let phantomLive = true;

    const clearInProgressIfThisLevel = (document: SaveDocument): SaveDocument =>
      document.inProgress?.gameId === gameId && document.inProgress.levelId === levelId
        ? { ...document, inProgress: null }
        : document;

    function buildCtx(savedState: unknown): GameContext {
      // PROG-067. The shell hands the game its own restored state a moment
      // ago -- that is what `savedState` already is -- so it must be treated
      // as the level's current state until the game reports otherwise, not
      // discarded until the first post-resume move. Seeded here, in
      // `buildCtx` itself, rather than where `savedState` is computed below:
      // this function runs twice on a refused resume (`PROG-032`), once with
      // the real value and once with `undefined` on the retry, and the retry
      // must reset `latestState` to `undefined` too -- the refused state was
      // never accepted and must not be written back. A restart's `savedState`
      // is already `undefined` (`forceFreshMount`), so all three paths --
      // resume, restart, refused-resume retry -- fall out of this one line
      // with no branch of their own.
      latestState = savedState;
      return {
        level,
        savedState,
        announce,
        onStateChange: (state) => {
          latestState = state;
        },
        onProgress: (progress: GameProgress) => {
          progressSignal.value = progress;
        },
        // SHELL-081. The one inbound channel `GameContext` carries
        // (`@contracts/game-module`'s own comment has the "why not an event
        // bus" reasoning) -- the game calls this once, at mount, to register
        // its own handler; the timer effect below is the only caller.
        onTimeExpired: (handler: () => void) => {
          timeExpiredHandlerRef.current = handler;
        },
        onComplete: (outcome: GameResult) => {
          if (torn) return;
          torn = true;
          levelInProgressSignal.value = false;
          // SHELL-047: unmounted before the result is shown, not alongside it.
          teardown();
          // PROG-033/PROG-003/SHELL-054: one write, not two -- clearing the
          // in-progress record and recording the completion are both true
          // the instant a level ends, and a crash between two separate
          // persist calls could apply one without the other. Fire-and-forget,
          // not awaited, the same shape `clearInProgressIfThisLevel` already
          // used alone here: "before displaying" means the write is
          // registered before `setResult` triggers the render, not that the
          // result screen waits on `storage.ts`'s own 500ms coalescing
          // (`PROG-009`) to actually land.
          // `persist`'s own mutator runs synchronously (`@shell/save`'s
          // `persist`), so capturing the record it produces here, for
          // `setStars` below, reads the value that was actually written --
          // not a second, possibly-stale computation of the same thing.
          let updatedRecord: LevelRecord | undefined;
          void persist((saved) => {
            // PROG-008/PWA-052: read before this attempt's own write lands --
            // completion.ts's own header has the reasoning for why the
            // document itself is the check, not a dedicated flag.
            if (isFirstCompletionEver(saved)) requestPersistentStorage();
            const cleared = clearInProgressIfThisLevel(saved);
            const gameProgress = recordCompletion(
              cleared.games[gameId],
              level.id,
              outcome,
              level.optionalGoals,
            );
            updatedRecord = gameProgress.levels[level.id];
            return {
              ...cleared,
              games: {
                ...cleared.games,
                [gameId]: gameProgress,
              },
            };
          });
          if (phantomLive) {
            // Consumed silently: the resulting popstate lands on `onPopState`
            // below with `torn` already true, so it neither asks nor moves
            // anything -- this only retires the guard before it can outlive
            // the level it belonged to.
            phantomLive = false;
            history.back();
          }
          setStars(
            updatedRecord
              ? deriveStars(updatedRecord.completed, updatedRecord.completedGoalSets.length)
              : 0,
          );
          setResult(outcome);
        },
      };
    }

    // SHELL-042: a restart is a fresh attempt regardless of how this mount
    // was originally reached. Reset immediately after reading -- exactly one
    // mount ignores `resumeChoice`, the one this effect run produces.
    const forceFreshMount = forceFreshMountRef.current;
    forceFreshMountRef.current = false;
    const savedState =
      !forceFreshMount && resumeChoice === 'resume' ? resumableHere?.state : undefined;

    try {
      teardown = readyGame.mount(host, buildCtx(savedState));
    } catch (error) {
      if (savedState === undefined) {
        // A genuine mount failure unrelated to resuming -- SHELL-006's job,
        // the same as before this stage. Nothing actually started playing,
        // so `levelInProgressSignal` (set below, only once mount has truly
        // succeeded) is correctly never set for this attempt.
        throw error;
      }
      // PROG-032. The resume attempt itself is what failed, not the level in
      // general: falling through to SHELL-006's boundary here would strand
      // the player behind a generic error with no way back to this exact
      // level, for a level that plays fine from the start. Retried once, not
      // wrapped in its own try/catch -- a level that fails to mount at all is
      // exactly the case above, and must still reach the boundary.
      setRestoreFailed(true);
      teardown = readyGame.mount(host, buildCtx(undefined));
    }

    // PWA-042: the same "in progress" this effect's own `torn` flag already
    // tracks, mirrored into a signal a screen outside this one can read --
    // see level-in-progress.ts's own header for why it is exactly these
    // three points and no others. After the try/catch above, not before: a
    // mount that throws and re-throws (a genuine crash) never actually
    // started a level, and must not leave this signal stuck true with no
    // cleanup ever registered to unset it.
    levelInProgressSignal.value = true;

    // PROG-005. Neither event alone covers every platform: `visibilitychange`
    // is what fires when a player backgrounds the app to take a call, but iOS
    // Safari can discard a backgrounded tab without ever firing it again, and
    // `pagehide` is what catches an actual navigation away or tab close --
    // including the ones bfcache later restores from, where `beforeunload`
    // does not fire reliably at all. Both funnel through the same save.
    //
    // `PROG-065`, ADR-0012: `persist`'s own write can lose a real race
    // against document teardown, measured in the resume-round-trip loop's
    // stage 0 -- a real `pagehide`-triggered reload never once let it
    // finish. `persistInProgressSync` alongside it is synchronous and was
    // the one thing in that measurement that reliably survived. Both are
    // called unconditionally, every time: `persist` still wins the common
    // case (the document stays reachable at boot from `bootState` without
    // needing `saveStore.load()`'s own reconciliation step at all), and the
    // sync write is what the teardown cases the async one loses actually
    // need.
    const persistInProgress = (): void => {
      // A level that has already ended has nothing "in progress" to save --
      // `onComplete` already tore the game down, and writing a snapshot after
      // that would resurrect a finished level as resumable.
      if (torn) return;
      const inProgress: InProgress = {
        gameId,
        levelId,
        state: latestState,
        savedAt: new Date().toISOString(),
      };
      persistInProgressSync(inProgress);
      void persist((saved) => ({ ...saved, inProgress }));
    };
    const onVisibilityChange = (): void => {
      if (document.visibilityState !== 'hidden') return;
      persistInProgress();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pagehide', persistInProgress);

    // SHELL-017. `popstate`, not `beforeunload`: this is specifically about
    // the router's own back navigation within the app, and `beforeunload`
    // does not fire for a same-document route change at all.
    //
    // `popstate` cannot be prevented, and cannot reliably be raced either --
    // preact-iso's own listener (registered once, at boot, ahead of this one)
    // always processes it first. Racing it by blocking the main thread while
    // asking turned out not to hold in a real browser the way it does in
    // theory, dialog or not; what actually works is the phantom history entry
    // pushed above, which turns the *first* back press into a no-op (an
    // identical-URL `popstate`, which preact-iso's own reducer bails out of
    // re-rendering for) rather than something that has to be raced or undone.
    // That non-event is this handler's cue to ask, via the dialog rather than
    // `window.confirm` (SHELL-019, and see LeaveConfirmDialog.tsx for why).
    // Staying re-arms the guard for next time. Leaving does not try to replay
    // whatever "back" would have reached -- it routes straight to level
    // select, the same destination every other way out of this screen
    // already uses (DATA-040, the module-load error, the result screen).
    const onPopState = (): void => {
      if (torn) return;
      phantomLive = false;
      setLeaveConfirm({
        onStay: () => {
          history.pushState(null, '', playUrl);
          phantomLive = true;
          setLeaveConfirm(null);
        },
        onLeave: () => {
          persistInProgress();
          setLeaveConfirm(null);
          location.route(routes.levelSelect(gameId), true);
        },
      });
    };
    window.addEventListener('popstate', onPopState);

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pagehide', persistInProgress);
      window.removeEventListener('popstate', onPopState);
      if (torn) return;
      torn = true;
      levelInProgressSignal.value = false;
      teardown();
    };
    // `resumableHere` and `location` are deliberately not in this list:
    // `resumableHere` only changes identity because `resumeChoice` (already
    // listed) changed, and `location` is preact-iso's own stable object for
    // the current app session. `progressSignal`/`remainingMsSignal` are
    // `useRef`-held and never change identity either. `restartTrigger`'s own
    // value is never read inside this effect -- only its change matters,
    // the same as `resumeChoice`'s siblings above it.
  }, [
    readyGame,
    readyLevel,
    gameId,
    levelId,
    resumeChoice,
    awaitingOffer,
    awaitingWarning,
    restartTrigger,
  ]);

  // SHELL-081. Deliberately its own effect, not folded into the mounting one
  // above: a pause/resume toggle must only start or stop an interval, never
  // tear down and remount the game itself (that would also call
  // `readyGame.mount` a second time, the exact re-render `SHELL-045` exists
  // to prevent applying to the mount call as much as to a render). `M3-091`
  // keeps the engine itself from ever owning this clock -- it lives here
  // because there is nowhere else for it to live.
  //
  // `restartTrigger` is listed even though nothing here reads its value: a
  // restart resets `remainingMsSignal` to `null` in the mounting effect
  // above, and this effect has to actually re-run to notice, tear down
  // whatever interval was already running against the pre-restart clock, and
  // reinitialise from `timeLimitMs` rather than reading the freshly-`null`
  // signal as "time's already up".
  useEffect(() => {
    if (!readyLevel || awaitingOffer || awaitingWarning || result) return;
    const timeLimitMs = readyLevel.constraints.timeLimitMs;
    if (timeLimitMs <= 0) {
      remainingMsSignal.value = null;
      return;
    }
    remainingMsSignal.value ??= timeLimitMs;
    if (paused) return;

    const interval = setInterval(() => {
      const current = remainingMsSignal.value;
      if (current === null) return;
      if (current <= 1000) {
        remainingMsSignal.value = 0;
        timeExpiredHandlerRef.current?.();
        return;
      }
      remainingMsSignal.value = current - 1000;
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [
    readyLevel,
    paused,
    result,
    awaitingOffer,
    awaitingWarning,
    restartTrigger,
    remainingMsSignal,
  ]);

  // A11Y-021/SHELL-041. `inert`, not merely a higher `z-index` on the
  // overlay above it -- an opaque or translucent overlay already blocks
  // pointer input by sitting on top, but does nothing about `Tab`, or an
  // assistive technology's own browse mode, still reaching an element
  // visually underneath it. `inert` is the one DOM primitive that pulls a
  // whole subtree out of both the tab order and the accessibility tree at
  // once, imperatively rather than through a JSX prop so this does not
  // depend on Preact's `inert` typing being current.
  //
  // Every one of this screen's four dialogs opens `aria-modal="true"`, so
  // all four gate this, not only `PauseOverlay` -- found by axe itself,
  // not planned: `tests/e2e/a11y.spec.ts`'s leave-confirm check started
  // reporting a new `color-contrast` `incomplete` the moment the HUD
  // existed for a modal to sit in front of, because axe cannot resolve
  // contrast with confidence for an element behind a translucent overlay
  // it cannot confirm is truly inert. The honest fix is not accepting that
  // finding -- the HUD's own colours are already well clear of 4.5:1 on
  // their own, measured directly, so the ambiguity was real but the
  // question underneath it was "should this content be reachable at all
  // while a modal covers it", not "is this contrast bad". Making it
  // properly `inert` answers that question rather than the axe symptom of
  // not having answered it, and removes the ambiguity by removing the
  // background from the accessibility tree axe was struggling to read
  // through, which is what modal background content should already be.
  const modalOpen = paused || restartConfirmOpen || quitConfirmOpen || leaveConfirm !== null;
  useEffect(() => {
    playAreaRef.current?.toggleAttribute('inert', modalOpen);
  }, [modalOpen]);

  const handleRestartConfirmed = (): void => {
    setRestartConfirmOpen(false);
    forceFreshMountRef.current = true;
    setRestartTrigger((n) => n + 1);
  };

  // SHELL-043. Discards, rather than the save-and-leave `persistInProgress`
  // every other exit from this screen uses -- `docs/prompts/sprint-5c.md`'s
  // own stage 1 note states the asymmetry: a deliberate exit (this, or
  // SHELL-042's restart) clears the record; an unexpected one (backgrounding,
  // `SHELL-017`'s back-navigation "Leave", which still means "leave, but keep
  // my progress") does not. Written directly, not through `persist(clearInProgressIfThisLevel)`
  // inside the mounting effect below -- that helper is effect-local and
  // rebuilt every mount for no reason this one needs, since it depends on
  // nothing but `gameId`/`levelId`, both already in scope here.
  const handleQuitConfirmed = (): void => {
    setQuitConfirmOpen(false);
    void persist((saved) =>
      saved.inProgress?.gameId === gameId && saved.inProgress.levelId === levelId
        ? { ...saved, inProgress: null }
        : saved,
    );
    location.route(routes.levelSelect(gameId), true);
  };

  // SHELL-051. The same restart mechanism `SHELL-042` already built, not a
  // second one: clearing `result` switches the render back to the mounting
  // branch below, and `forceFreshMountRef`/`restartTrigger` make that mount
  // a fresh attempt the identical way a mid-play restart does. "Replay" and
  // "restart" are the same action from two different screens, not two
  // features that happen to look alike.
  const handleReplay = (): void => {
    setResult(null);
    forceFreshMountRef.current = true;
    setRestartTrigger((n) => n + 1);
  };

  if (moduleState.status === 'loading') {
    return (
      <Screen title="Play">
        <p>Loading&hellip;</p>
      </Screen>
    );
  }

  if (moduleState.status === 'error') {
    return (
      <Screen title="Play">
        <p>{loadFailureMessage(moduleState.failure)}</p>
        <nav class="screen-nav">
          <button type="button" onClick={moduleState.retry}>
            Try again
          </button>
          <a class="nav-link" href={routes.picker()}>
            Back to the game picker
          </a>
        </nav>
      </Screen>
    );
  }

  if (levelState.status === 'loading') {
    return (
      <Screen title="Play">
        <p>Loading the level&hellip;</p>
      </Screen>
    );
  }

  if (levelState.status === 'error') {
    // DATA-040: report the level as unloadable and offer the way back level
    // select requires, rather than leaving the player stranded on this route.
    return (
      <Screen title="Play">
        <p>{loadFailureMessage(levelState.failure)}</p>
        <nav class="screen-nav">
          <a class="nav-link" href={routes.levelSelect(gameId)}>
            Back to level select
          </a>
        </nav>
      </Screen>
    );
  }

  if (!readyLevel) {
    // Unreachable in practice: every branch above already returned unless
    // `levelState.status === 'ready'`, which is the only case `readyLevel`
    // is undefined for. A defensive return rather than a non-null
    // assertion, since both `result` and the main branch below need
    // `readyLevel.goals`/`.constraints`.
    return null;
  }

  if (result) {
    // SHELL-052: `readyIndex` is the real level index this attempt already
    // fetched (`DATA-031`), not `platform/game-registry.ts`'s flat
    // `levelCount` -- see `nextLevel`'s own comment for why that number is
    // the wrong shape for "next".
    const next = readyIndex ? nextLevel(readyIndex, readyLevel.id) : null;
    return (
      <Screen title="Play">
        <ResultScreen
          result={result}
          goals={readyLevel.goals}
          stars={stars}
          onReplay={handleReplay}
          nextHref={next ? routes.play(gameId, next.id) : undefined}
          levelSelectHref={routes.levelSelect(gameId)}
        />
      </Screen>
    );
  }

  // PROG-030.
  if (awaitingOffer) {
    return (
      <Screen title="Play">
        <p>You have progress saved here. Resume where you left off?</p>
        <nav class="screen-nav">
          <button
            type="button"
            onClick={() => {
              setResumeChoice('resume');
            }}
          >
            Resume
          </button>
          <button
            type="button"
            onClick={() => {
              void persist((document) =>
                document.inProgress?.gameId === gameId && document.inProgress.levelId === levelId
                  ? { ...document, inProgress: null }
                  : document,
              );
              setResumeChoice('fresh');
            }}
          >
            Start fresh
          </button>
        </nav>
      </Screen>
    );
  }

  // PROG-035.
  if (awaitingWarning) {
    return (
      <Screen title="Play">
        <p>
          Starting this level will discard your saved progress in another level. That progress will
          be lost.
        </p>
        <nav class="screen-nav">
          <button
            type="button"
            onClick={() => {
              setResumeChoice('fresh');
            }}
          >
            Continue
          </button>
          <a class="nav-link" href={routes.levelSelect(gameId)}>
            Cancel
          </a>
        </nav>
      </Screen>
    );
  }

  return (
    <Screen title="Play">
      {restoreFailed && (
        <p class="save-notice">Your saved progress here could not be restored. Starting fresh.</p>
      )}
      <div ref={playAreaRef} class="play-area">
        <Hud
          goals={readyLevel.goals}
          moveLimit={readyLevel.constraints.moveLimit}
          progress={progressSignal}
          remainingMs={remainingMsSignal}
          onPause={() => {
            setPaused(true);
          }}
          onRestart={() => {
            setRestartConfirmOpen(true);
          }}
          onQuit={() => {
            setQuitConfirmOpen(true);
          }}
        />
        <div ref={hostRef} class="play-host" />
      </div>
      {paused && (
        <PauseOverlay
          onResume={() => {
            setPaused(false);
          }}
        />
      )}
      {restartConfirmOpen && (
        <RestartConfirmDialog
          onCancel={() => {
            setRestartConfirmOpen(false);
          }}
          onConfirm={handleRestartConfirmed}
        />
      )}
      {quitConfirmOpen && (
        <QuitConfirmDialog
          onCancel={() => {
            setQuitConfirmOpen(false);
          }}
          onConfirm={handleQuitConfirmed}
        />
      )}
      {leaveConfirm && (
        <LeaveConfirmDialog onStay={leaveConfirm.onStay} onLeave={leaveConfirm.onLeave} />
      )}
    </Screen>
  );
}
